import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/ui/Button';
import { AlertCircle, TrendingUp, CheckCircle2, AlertTriangle, Zap, Clock, Award, Sparkles } from 'lucide-react';
import { calculateXpReward } from '@/services/rewardService';
import { useAuth } from '@/contexts/AuthContext';

export default function AnalysisResults({ analysis, onNewAnalysis }) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [showBonusOffer, setShowBonusOffer] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(60); // 60 seconds
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [trainingStarted, setTrainingStarted] = useState(false);
  const [xpReward, setXpReward] = useState(null);
  const [bonusMessage, setBonusMessage] = useState(null);

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No analysis to display</p>
      </div>
    );
  }

  const { 
    gap_analysis,
    discovery_score, 
    objection_handling_score, 
    closing_score, 
    overall_score,
    improvement_suggestions,
    conversation_type,
    created_at,
    industry,
    client_text
  } = analysis;

  const clientText = analysis?.client_text || analysis?.clientText || null;

  // Calculate reward eligibility (no XP awarded here)
  useEffect(() => {
    if (!user || !overall_score) return;

    const reward = calculateXpReward(overall_score, 'conversationAnalysis');
    setXpReward(reward);
    
    if (!reward.eligible) {
      setBonusMessage(reward.message);
    }
  }, [user, overall_score]);

  // Timer for bonus offer
  useEffect(() => {
    if (!showBonusOffer) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setShowBonusOffer(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showBonusOffer]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Communication improvement suggestions with example responses
  const communicationImprovements = {
    discovery_gaps: {
      title: 'Improve Your Discovery Questions',
      icon: AlertCircle,
      color: 'blue',
      improvement: 'Instead of asking closed questions, use open-ended questions to uncover deeper needs.',
      example: '❌ "Do you have budget for this?" → ✅ "Walk me through your current process and where you see the biggest bottlenecks."',
      training: { id: 'discovery-mastery', name: 'Discovery Mastery', points: 50, duration: '15 min' },
      focus: 'discovery_gaps'
    },
    objection_gaps: {
      title: 'Improve Your Objection Handling',
      icon: AlertTriangle,
      color: 'orange',
      improvement: 'Validate the prospect\'s concern first, then provide evidence-based solutions.',
      example: '❌ "That\'s not true, we\'re actually cheaper." → ✅ "I understand budget is a concern. Let me show you how our clients see ROI in 90 days."',
      training: { id: 'objection-mastery', name: 'Objection Mastery', points: 50, duration: '12 min' },
      focus: 'objection_gaps'
    },
    closing_gaps: {
      title: 'Improve Your Closing Approach',
      icon: CheckCircle2,
      color: 'green',
      improvement: 'Use trial closes to gauge interest and create clear next-step commitments.',
      example: '❌ "So what do you think?" → ✅ "Based on what we discussed, it sounds like this solves your main challenge. Does Tuesday or Thursday work better to set up the implementation call?"',
      training: { id: 'closing-mastery', name: 'Closing Mastery', points: 50, duration: '10 min' },
      focus: 'closing_gaps'
    },
    listening_gaps: {
      title: 'Improve Your Active Listening',
      icon: AlertCircle,
      color: 'purple',
      improvement: 'Pause to let the prospect fully express themselves before responding.',
      example: '❌ Interrupting to present features → ✅ "Tell me more about that. What else is affecting your decision?"',
      training: { id: 'listening-skills', name: 'Active Listening Skills', points: 40, duration: '8 min' },
      focus: 'listening_gaps'
    },
    confidence_gaps: {
      title: 'Build More Confidence',
      icon: TrendingUp,
      color: 'indigo',
      improvement: 'Speak with authority about your solution\'s value without being pushy.',
      example: '❌ "Um, I think maybe our solution could help?" → ✅ "Our solution directly addresses this, and here\'s how it works..."',
      training: { id: 'confidence-selling', name: 'Confident Selling', points: 45, duration: '10 min' },
      focus: 'confidence_gaps'
    },
    follow_up_gaps: {
      title: 'Improve Your Follow-up Strategy',
      icon: Zap,
      color: 'yellow',
      improvement: 'Set specific expectations for follow-up instead of leaving it vague.',
      example: '❌ "I\'ll reach out later." → ✅ "I\'ll send you that case study by EOD tomorrow, and we\'ll hop on a call Thursday at 2pm to discuss next steps."',
      training: { id: 'follow-up-mastery', name: 'Follow-up Mastery', points: 35, duration: '7 min' },
      focus: 'follow_up_gaps'
    }
  };

  const buildSuggestedReplies = (text) => {
    if (!text) return [];
    const generic = [
      `I hear you on "${text.slice(0, 80)}..." — let me make sure I address that directly. Can I clarify your top goal here?`,
      'Great question. Here is the short answer, then I can share a quick example so it is concrete for you.',
      'If I were in your shoes, I would want proof. How about I show you a 2-minute example and a quick ROI snapshot?'
    ];

    // Tailor by top gap to coach the response style
    if (topImprovement?.focus === 'objection_gaps' || gap_analysis?.includes?.('objection')) {
      generic.push('Totally fair point. When you say that, what have you seen in the past? If I can show how we avoid that, would it help?');
    }
    if (topImprovement?.focus === 'closing_gaps') {
      generic.push('Based on what we discussed, does it make sense to schedule a quick working session this week or next?');
    }
    if (topImprovement?.focus === 'discovery_gaps') {
      generic.push('Can you walk me through what happens today from start to finish so I do not miss anything important?');
    }

    return generic.slice(0, 3);
  };

  const handleStartTraining = (trainingId) => {
    if (!user) {
      navigate('/authentication');
      return;
    }

    setSelectedTraining(trainingId);
    setTrainingStarted(true);
    setShowBonusOffer(false);

    // Navigate to training page; XP is awarded on completion only
    setTimeout(() => {
      navigate(`/training/${trainingId}`, { state: { fromAnalysis: true, bonus: xpReward?.eligible, weakestAreas } });
    }, 300);
  };

  const ScoreCard = ({ label, score, icon: Icon, description }) => {
    const getColor = (score) => {
      if (score >= 80) return 'bg-green-50 border-green-200';
      if (score >= 60) return 'bg-yellow-50 border-yellow-200';
      return 'bg-red-50 border-red-200';
    };

    const getScoreColor = (score) => {
      if (score >= 80) return 'text-green-700';
      if (score >= 60) return 'text-yellow-700';
      return 'text-red-700';
    };

    return (
      <div className={`p-4 rounded-lg border ${getColor(score)}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <Icon className="w-4 h-4 text-gray-400" />
        </div>
        <p className={`text-3xl font-bold ${getScoreColor(score)}`}>
          {score}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
          <div
            className={`h-1.5 rounded-full transition-all ${
              score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
    );
  };

  // Get top improvement area based on lowest score
  let topImprovement = null;
  const lowestScore = Math.min(discovery_score, objection_handling_score, closing_score);
  if (lowestScore === discovery_score) topImprovement = communicationImprovements.discovery_gaps;
  else if (lowestScore === objection_handling_score) topImprovement = communicationImprovements.objection_gaps;
  else if (lowestScore === closing_score) topImprovement = communicationImprovements.closing_gaps;

  const weakestAreas = [
    { label: 'Discovery', score: discovery_score },
    { label: 'Objection Handling', score: objection_handling_score },
    { label: 'Closing', score: closing_score }
  ].sort((a, b) => a.score - b.score);

  return (
    <div className="w-full max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Analysis Results</h3>
          <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
            {conversation_type === 'call_transcript' ? '📞 Call' : '📧 Email'} • {industry}
          </span>
        </div>
        <p className="text-xs text-gray-500">
          Analyzed on {formatDate(created_at)}
        </p>
      </div>

      {/* Overall Score */}
      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">Overall Performance</p>
          <TrendingUp className="w-5 h-5 text-blue-600" />
        </div>
        <p className="text-4xl font-bold text-blue-700">{overall_score}/100</p>
        <p className="text-xs text-gray-600 mt-2">
          {overall_score >= 80
            ? '✅ Excellent execution of sales frameworks and techniques'
            : overall_score >= 60
            ? '⚠️ Solid foundation with specific areas for improvement'
            : '🎯 Strong opportunity for growth with focused practice'}
        </p>
      </div>

      {/* Score Breakdown */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Performance by Stage</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ScoreCard 
            label="Discovery" 
            score={discovery_score} 
            icon={AlertCircle}
            description="How well you identified prospect needs"
          />
          <ScoreCard 
            label="Objection Handling" 
            score={objection_handling_score} 
            icon={AlertTriangle}
            description="How effectively you addressed concerns"
          />
          <ScoreCard 
            label="Closing" 
            score={closing_score} 
            icon={CheckCircle2}
            description="How well you advanced commitment"
          />
        </div>
      </div>

      {/* BONUS: Limited Time Training Offer - ONLY IF ELIGIBLE */}
      {showBonusOffer && topImprovement && !trainingStarted && xpReward?.eligible && (
        <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-amber-300 relative overflow-hidden">
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
            <Clock className="w-3 h-3" />
            {timeRemaining}s
          </div>
          <div className="flex items-start gap-3 mb-3">
            <Award className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-900">⏰ Bonus Opportunity - Limited Time!</p>
              <p className="text-xs text-amber-800 mt-1">
                You scored <span className="font-bold">{overall_score}%</span> and unlocked <span className="font-bold">+{topImprovement.training.points} bonus XP</span> when you finish this training.
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Complete the recommended training right now to earn this bonus
              </p>
            </div>
          </div>
          <div className="bg-white rounded p-3 mb-3 border border-amber-200">
            <p className="text-sm font-semibold text-gray-900 mb-2">📚 {topImprovement.training.name}</p>
            <p className="text-xs text-gray-600">⏱️ {topImprovement.training.duration}</p>
          </div>
          <Button 
            onClick={() => handleStartTraining(topImprovement.training.id)}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
          >
            {`Start Training Now (+${topImprovement.training.points} XP)`}
          </Button>
          <p className="text-xs text-amber-700 mt-2 text-center">This offer expires in {timeRemaining} seconds</p>
        </div>
      )}

      {/* MOTIVATIONAL MESSAGE - IF NOT ELIGIBLE */}
      {!xpReward?.eligible && bonusMessage && (
        <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900 mb-1">Keep Building Your Skills! 💪</p>
              <p className="text-sm text-blue-800">{bonusMessage}</p>
              <p className="text-xs text-blue-700 mt-2">
                💡 Aim for <strong>75%+</strong> on your next attempt to unlock bonus XP rewards and special training offers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Areas for Improvement - Communication Focused */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-orange-600" />
          <h4 className="text-sm font-semibold text-gray-900">How to Improve Your Sales Communication</h4>
        </div>
        <div className="space-y-4">
          {topImprovement && (
            <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-300">
              <div className="flex items-start gap-3">
                <topImprovement.icon className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-orange-900 mb-2">{topImprovement.title}</p>
                  <p className="text-sm text-gray-700 mb-3"><strong>Insight:</strong> {topImprovement.improvement}</p>
                  <div className="bg-white rounded p-3 mb-3 border border-orange-200">
                    <p className="text-xs font-mono text-gray-600">{topImprovement.example}</p>
                  </div>
                  <Button 
                    variant="outline"
                    className="text-sm"
                    onClick={() => handleStartTraining(topImprovement.training.id)}
                  >
                    Learn More: {topImprovement.training.name}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Suggested Replies for Client Text */}
      {clientText && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <h4 className="text-sm font-semibold text-gray-900">Suggested Replies to Client Text</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {buildSuggestedReplies(clientText).map((reply, idx) => (
              <div key={idx} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Suggestion #{idx + 1}</p>
                <p className="text-sm text-gray-800 leading-relaxed">{reply}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Improvement Suggestions */}
      {improvement_suggestions && improvement_suggestions.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <h4 className="text-sm font-semibold text-gray-900">Recommended Next Steps</h4>
          </div>
          <ul className="space-y-2">
            {improvement_suggestions.map((suggestion, idx) => (
              <li key={idx} className="flex gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-green-600 font-bold flex-shrink-0 text-sm">{idx + 1}.</span>
                <span className="text-sm text-gray-700">{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Training Recommendations */}
      <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
        <h4 className="text-sm font-semibold text-indigo-900 mb-3">📚 Personalized Training Path</h4>
        <div className="space-y-2">
          {discovery_score < 70 && (
            <p className="text-sm text-indigo-700">
              🎯 <strong>Discovery Mastery:</strong> Learn to ask powerful questions that uncover prospect needs and create urgency.
            </p>
          )}
          {objection_handling_score < 70 && (
            <p className="text-sm text-indigo-700">
              🎯 <strong>Objection Mastery:</strong> Master techniques to validate concerns and turn objections into opportunities.
            </p>
          )}
          {closing_score < 70 && (
            <p className="text-sm text-indigo-700">
              🎯 <strong>Closing Mastery:</strong> Develop confident closing techniques and clear next-step commitments.
            </p>
          )}
          {overall_score >= 80 && (
            <p className="text-sm text-indigo-700">
              🎯 <strong>Advanced Selling:</strong> You're executing well! Explore advanced strategies for complex sales cycles.
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button onClick={onNewAnalysis} variant="outline" className="flex-1">
          New Analysis
        </Button>
        <Button className="flex-1">
          Download Report
        </Button>
      </div>
    </div>
  );
}
